import React from 'react';
import { UserIcon } from '@heroicons/react/24/outline';
import PlaybookSection from '../../../components/playbooks/PlaybookSection';
import PlaybookCard from '../../../components/playbooks/PlaybookCard';
import ExpandableCallout from '../../../components/playbooks/ExpandableCallout';
import PlaybookImage from '../../../components/playbooks/PlaybookImage';

const SettingBoundariesStage1 = () => (
  <div className="space-y-8">
    <PlaybookSection title="Stage 1: It starts with you" icon={<UserIcon className="h-7 w-7 text-primary-light" />} heading>
      {/* TL;DR */}
      <PlaybookCard>
        <h2 className="text-xl font-semibold mb-2 ">TL;DR</h2>
        <ExpandableCallout type="info" title="What do I need to DO in this stage?" defaultOpen>
          <p className="mb-4">You need to do 1 thing:</p>
          <ol className="list-decimal pl-6 space-y-2 mb-4">
            <li>Clarify the scope of <strong>your</strong> role.</li>
          </ol>
          <p className="mb-2">Here's what we mean…</p>
        </ExpandableCallout>
      </PlaybookCard>

      {/* Clarify the scope of your role */}
      <PlaybookCard>
        <h3 className="text-lg font-semibold mb-2">Clarify the scope of <strong>your</strong> role.</h3>
        <p className="mb-4">This may sound obvious, but it's easy to forget to do this and if you're not clear with founder/CEO then that lack of clarity ripples across your team.</p>
        <div className="mb-4">
          <div className="font-semibold mb-1">The goals here are:</div>
          <ul className="list-disc pl-6 space-y-1">
            <li>Align you &lt;&gt; founder/CEO on your core accountabilities</li>
            <li>Check priorities</li>
            <li>Check desired results</li>
          </ul>
        </div>
        <div className="mb-4">
          <div className="font-semibold mb-1">Action:</div>
          <ul className="list-disc pl-6 space-y-1">
            <li>In your next 1-2-1 check in with founder/CEO/line manager.</li>
            <li>Share what you think your accountabilities are and your goals/focus ratio should be.</li>
            <li>Check against their expectations for your role (and by extension your team).</li>
          </ul>
        </div>
        <ExpandableCallout type="tip" title="Here's an example structure you could follow for your 1-pager / Notion doc:" defaultOpen>
          <p className="text-primary-light mb-4">–This is just one way to structure your document - feel free to adapt it to what works best for you and your organization.</p>
          <div className="prose prose-invert max-w-none space-y-4">
            <h4 className="font-semibold">My principle accountabilities are to:</h4>
            <ol className="list-decimal pl-6">
              <li>Build, hire and retain a world class team.</li>
              <li>Support our team throughout the entire employee lifecycle.</li>
              <li>Ensure GA burn ratio to revenue remains within 5% of target.</li>
            </ol>
            <h4 className="font-semibold">My primary priorities right now are to:</h4>
            <ol className="list-decimal pl-6">
              <li>Ensure onboarding experience sets new team members up for success</li>
              <li>Hire the following roles (xyz)</li>
              <li>Plan and run Q2 offsite</li>
            </ol>
            <h4 className="font-semibold">My secondary* priorities right now are to:</h4>
            <p className="italic text-sm">*important to us but not to be delivered at the expense of primaries.</p>
            <ol className="list-decimal pl-6">
              <li>Improve the feedback loop between team members &lt;&gt; people managers &lt;&gt; people team</li>
              <li>Revamp career site</li>
              <li>Develop and publish career framework</li>
            </ol>
            <h4 className="font-semibold">Broadly speaking my team's pie* should be:</h4>
            <p className="italic text-sm">*Example per stage included for reference</p>
            <div className="flex flex-col sm:flex-row gap-4 my-4">
              <div className="flex-1">
                <PlaybookImage
                  src="https://auth.openorg.fyi/storage/v1/object/public/resources/setting-boundaries/earlstage.png"
                  alt="Early stage team pie chart example"
                  caption="Example: Early stage team focus distribution"
                />
              </div>
              <div className="flex-1">
                <PlaybookImage
                  src="https://auth.openorg.fyi/storage/v1/object/public/resources/setting-boundaries/earlstage.png"
                  alt="Early stage team pie chart example 2"
                  caption="Example: Later stage team focus distribution"
                />
              </div>
            </div>
            <div className="text-xs text-gray-400 mt-2">
              Source: <a href="https://peopleandculturepickles.substack.com/" target="_blank" rel="noopener noreferrer" className="underline hover:text-primary">Luke Shipley's Substack: People And Culture Pickles</a>
            </div>
            <h4 className="font-semibold">Budget is:</h4>
            <p>£120k</p>
          </div>
        </ExpandableCallout>
        <ExpandableCallout type="tip" title="This kind of clarity helps you understand whether there's any misalignment between you &lt;&gt; leadership but also sets a strong foundation for leading your team and helping them to set the right boundaries." defaultOpen />
      </PlaybookCard>

      {/* Outcome */}
      <PlaybookCard>
        <h3 className="text-lg font-semibold mb-2">Outcome</h3>
        <ul className="list-disc pl-6">
          <li>100% alignment between you &lt;&gt; leadership on your role and your team.</li>
        </ul>
      </PlaybookCard>
    </PlaybookSection>
  </div>
);

export default SettingBoundariesStage1; 